import { type ReactQuery } from 'cms-api'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import 'swiper/css/bundle'
import { Box, Container, Flex, Grid } from 'components/Layout'
import { Text } from 'components/Typography'
import { StrapiBrandsWidgetCarousel } from 'components/Widgets/StrapiBrandsWidget/StrapiBrandsWidgetCarousel'
import { COLOR_INTENT, FONT_STACK, FONT_WEIGHT, mq, SPACE } from 'Theme'
import { TRADE_OUR_BRANDS } from '../tradePerksData'

const { TRADE_PAGE } = COLOR_INTENT.WIDGETS

const MAX_CAROUSEL_SLIDES = {
  MOBILE: 2,
  TABLET: 4,
  DESKTOP: 6,
}

const Eyebrow = styled(Text)`
  text-transform: uppercase;
  letter-spacing: 0.18em;
`

const BrandCard = styled(Flex)`
  flex-direction: column;
  border: 1px solid ${TRADE_PAGE.BORDER_CARD};
  background: ${TRADE_PAGE.CARD_BG};
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${TRADE_PAGE.ACCENT};
  }
`

const Divider = styled(Box)`
  width: ${rem(26)};
  height: 1px;
  background: ${TRADE_PAGE.ICON_RING};
  margin: ${rem(18)} auto ${rem(20)};
`

const BRAND_LOGO_BASE_HEIGHT = {
  MOBILE: 13,
  TABLET: 16,
}

// Fixed-height row keeps the dividers below aligned across cards even
// though each logo gets a different optically-corrected height
const BrandLogoRow = styled(Flex)`
  align-items: center;
  justify-content: center;
  height: ${rem(BRAND_LOGO_BASE_HEIGHT.MOBILE * 1.5)};

  ${mq.from.TABLET`
    height: ${rem(BRAND_LOGO_BASE_HEIGHT.TABLET * 1.5)};
  `}
`

const BrandLogo = styled.img<{ $scale: number }>`
  display: block;
  height: ${({ $scale }) => rem(BRAND_LOGO_BASE_HEIGHT.MOBILE * $scale)};
  max-width: 100%;
  object-fit: contain;

  ${({ $scale }) => mq.from.TABLET`
    height: ${rem(BRAND_LOGO_BASE_HEIGHT.TABLET * $scale)};
  `}
`

export const TradePlatformSection: React.FC<{
  marketplaceBrands: ReactQuery.WidgetsBrandsItemComponent[] | null
}> = ({ marketplaceBrands }) => {
  const hasMarketplaceBrands = Boolean(marketplaceBrands?.length)

  return (
    <Box backgroundColor={TRADE_PAGE.SECTION_BG}>
      <Container
        pt={{ MOBILE: rem(52), TABLET: rem(80) }}
        pb={0}
        px={{ MOBILE: SPACE.PX_24, TABLET: SPACE.PX_40 }}
      >
        <Text
          as="h2"
          fontFamily={FONT_STACK.SERIF}
          fontWeight={FONT_WEIGHT.NORMAL}
          fontSize={{ MOBILE: rem(28), TABLET: rem(34) }}
          color={TRADE_PAGE.HEADING}
          mb={SPACE.PX_6}
        >
          One platform. Your favorite brands.
        </Text>
        <Text
          fontSize={{ MOBILE: rem(14), TABLET: rem(15) }}
          lineHeight="1.6"
          color={TRADE_PAGE.TEXT_MUTED}
          mb={{ MOBILE: SPACE.PX_24, TABLET: rem(40) }}
          style={{ maxWidth: rem(620) }}
          as="p"
        >
          Four distinct brands and 200+ curated makers—all under one trade
          membership that works across every one of them.
        </Text>

        {hasMarketplaceBrands && (
          <Flex
            alignItems="baseline"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={rem(20)}
            mb={SPACE.PX_24}
          >
            <Eyebrow
              fontSize={rem(11)}
              fontWeight={FONT_WEIGHT.SEMI_BOLD}
              color={TRADE_PAGE.EYEBROW}
            >
              The marketplace
            </Eyebrow>
            <Text
              fontSize={rem(13)}
              color={TRADE_PAGE.TEXT_SOFT}
              textAlign={{ TABLET: 'right' }}
              display={{ MOBILE: 'none', TABLET: 'block' }}
              style={{ maxWidth: rem(460) }}
            >
              From furniture and lighting to rugs and fabric, discover hundreds
              of curated brands available through your membership.
            </Text>
          </Flex>
        )}
      </Container>

      {/* Carousel stays outside Container - it derives its own gutter from
          viewport width and intentionally bleeds past the layout edge */}
      {hasMarketplaceBrands && (
        <Box mb={{ MOBILE: rem(28), TABLET: rem(56) }}>
          <Box display={{ MOBILE: 'block', TABLET: 'none', DESKTOP: 'none' }}>
            <StrapiBrandsWidgetCarousel
              brands={marketplaceBrands}
              maxSlidesCount={MAX_CAROUSEL_SLIDES.MOBILE}
            />
          </Box>
          <Box display={{ MOBILE: 'none', TABLET: 'block', DESKTOP: 'none' }}>
            <StrapiBrandsWidgetCarousel
              brands={marketplaceBrands}
              maxSlidesCount={MAX_CAROUSEL_SLIDES.TABLET}
            />
          </Box>
          <Box display={{ MOBILE: 'none', DESKTOP: 'block' }}>
            <StrapiBrandsWidgetCarousel
              brands={marketplaceBrands}
              maxSlidesCount={MAX_CAROUSEL_SLIDES.DESKTOP}
            />
          </Box>
        </Box>
      )}

      <Container
        pb={{ MOBILE: rem(52), TABLET: rem(80) }}
        px={{ MOBILE: SPACE.PX_24, TABLET: SPACE.PX_40 }}
      >
        <Eyebrow
          fontSize={rem(11)}
          fontWeight={FONT_WEIGHT.SEMI_BOLD}
          color={TRADE_PAGE.EYEBROW}
          pt={{ MOBILE: rem(28), TABLET: rem(40) }}
          mb={{ MOBILE: rem(14), TABLET: rem(18) }}
          style={{
            borderTop: `1px solid ${TRADE_PAGE.BORDER}`,
            display: 'block',
          }}
        >
          Our brands
        </Eyebrow>

        <Grid
          gridTemplateColumns={{ MOBILE: '1fr 1fr', DESKTOP: 'repeat(4, 1fr)' }}
          gap={{ MOBILE: rem(12), TABLET: rem(20) }}
        >
          {TRADE_OUR_BRANDS.map((brand) => (
            <BrandCard
              key={brand.name}
              as="a"
              href={brand.href}
              target="_blank"
              rel="noopener"
              px={{ MOBILE: rem(14), TABLET: rem(26) }}
              py={{ MOBILE: rem(22), TABLET: rem(42) }}
              minHeight={{ TABLET: rem(190) }}
            >
              <BrandLogoRow>
                <BrandLogo
                  src={brand.logoSrc}
                  alt={brand.name}
                  $scale={brand.logoScale}
                />
              </BrandLogoRow>
              <Divider />
              <Text
                as="p"
                fontSize={{ MOBILE: rem(12), TABLET: rem(13) }}
                lineHeight="1.55"
                color={TRADE_PAGE.TEXT_SOFT}
                textAlign="center"
                mb={0}
              >
                {brand.desc}
              </Text>
            </BrandCard>
          ))}
        </Grid>
      </Container>
    </Box>
  )
}
